<script setup lang="ts">
import { requiredValidator } from "@/@core/utils/validators";
import { pb } from "@/utils/PocketBaseAdapter";
import { useUserStore } from "@/utils/UserStore";
import { moment } from "@/utils/useTimeZone";
import { useUserRole } from "@/utils/useUserRole";
import { toast } from "vue3-toastify";
import { VForm } from "vuetify/components/VForm";

const props = defineProps<{
  isDialogOpen: boolean;
  email: string;
  importedID: string;
  password: string | number;
}>();

interface Emit {
  (e: "closeDialog"): void;
}

// Emits
const emit = defineEmits<Emit>();

// refs
const verifyStepVForm = ref<VForm>();
const verificationCode = ref<string>();
const errors = ref<Record<string, string | undefined>>({
  code: "",
});

// stores
const userStore = useUserStore();
const userRoleStore = useUserRole();
const { currentLocation } = storeToRefs(userRoleStore);
const { getUserRolePermission } = userRoleStore;

// router
const router = useRouter();

const directDebitEnabled = ref(false);
const CanDoDirectDebit = async () => {
  // Get Users Location
  try {
    let userValues = await pb.collection("users").getOne(props.importedID);

    if (userValues && userValues.homeLocation) {
      let location = await pb
        .collection("locations")
        .getOne(userValues.homeLocation);
      if (location) {
        directDebitEnabled.value = location.onlineDirectDebitEnabled
          ? true
          : false;
      }
    }
  } catch (e) {}
  return false;
};

/**
 * Handles the validation process for two-factor authentication.
 * It retrieves the user's record, verifies the entered verification code,
 * and updates the user's data accordingly.
 *
 * @returns {Promise<void>}
 */
const handleValidation = async () => {
  try {
    // Validate the form
    let { valid } = await verifyStepVForm.value?.validate();
    if (valid) {
      // Get the current user's record
      const record = await pb.collection("users").getOne(props.importedID);

      await CanDoDirectDebit();

      // Get the verification code from the user's data
      const currVerificationCode = record.twoFACode;

      // Verify the entered verification code
      if (currVerificationCode == verificationCode.value) {

        // Clear any previous error messages
        errors.value.code = "";

        let userData = {};
        // Update the user's data
        userData.lastLogin = moment().toISOString();

        userData.twoFACode = '';
        // TODO Confirm this works
        // userData.verified = true;
        userData.emailVisibility = true;
        userData.status = "ImpCompleted";
        userData.oldPassword = "!qaz2wsX";
        userData.password = props.password;
        userData.passwordConfirm = props.password;

        // Update the user's record in the database
        try {
          await pb.collection("users").update(props.importedID, userData);
        } catch (e) {
          // USer may have already changed their password
        }

        // LOG THE USER IN NOW
        let response = await pb
          .collection("users")
          .authWithPassword(props.email.toLowerCase(), props.password);

        // Store the user's email in local storage
        localStorage.setItem("email", response.record?.email);

        // Update the current location in the user store
        currentLocation.value = response.record?.homeLocation;
        userStore.currentLocation = response.record?.homeLocation;

        // Fetch the user's role permissions
        await getUserRolePermission();

        // Does the User Have a Membership?
        if (response.record?.renewMembership != "") {
          let records = await pb.collection("bankCards").getFullList({
            sort: "-isPrimary",
            filter: `user = '${props.importedID}'`,
          });

          if (records && records.length > 0) {
            closeVerifyCC(); // We have a card - just let them in
          } else {
            try {
              let membershipCostCheck = await pb.collection("memberships").getOne(response.record.renewMembership)
              if (membershipCostCheck && membershipCostCheck.recouringFee && membershipCostCheck.recouringFee > 0) {
                verifyCC.value = true;
              } else {
                closeVerifyCC();
              }
            }catch(e){}
          }
        } else {
          closeVerifyCC();
        }
        // Navigate to the home page
        //router.push("/user/" + props.importedID);
        //router.push("/")
      } else {
        // Display an error message if the verification code is incorrect
        errors.value.code = "Verification Code is Wrong!!";
        // toast.error("Verification Code is Wrong!!", {
        //   position: toast.POSITION.BOTTOM_RIGHT,
        // });
      }
    }
  } catch (error) {
    // Log any errors that occur during the process
  }
};

/**
 * Handles the resend of a verification code to the user's email.
 * This function is called when the user clicks the "Resend" link in the two-factor authentication dialog.
 * It retrieves the user's record, sets the user's status and two-factor code to prepare for a new verification code,
 * and updates the user's data in the database.
 *
 * @returns {Promise<void>} - Returns a promise that resolves when the operation is completed successfully.
 * If an error occurs during the process, it will be logged to the console.
 */
const handleCodeResend = async () => {
  hasRequested.value = true;
  try {
    let data = {
      status: "ImpValidating",
      twoFACode: '',
    };

    // Update the user's record in the database
    await pb.collection("users").update(props.importedID, data);

    // Display a success message to the user
    toast.info("New verification code has been sent 👍", {
      position: toast.POSITION.BOTTOM_RIGHT,
    });
  } catch (error) {
    // Log any errors that occur during the process
  }
};

const hasRequested = ref(true);

//ADD CARD DETAILS
const verifyCC = ref(false);
const closeVerifyCC = async () => {
  // Update User
  verifyCC.value = false;
  let userData = {};
  userData.status = "Active";
  userData.lastLogin = moment().toISOString();
  await pb.collection("users").update(props.importedID, userData);
  let location = window.location.href; // Hard reload the current page
  window.location.href = location
    .toString()
    .replace("/login", "/user/" + props.importedID);
};

const addCardDialogMessage = ref({
  isVisible: false,
  message: "",
  color: "",
  spinner: false,
});

const isAddCardDialogVisible = ref(false);
const handleAddCard = async (): Promise<void> => {
  // Update User To be Active - Import Complete
  verifyCC.value = false;
  let userData = {};
  userData.status = "Active";
  userData.lastLogin = moment().toISOString();
  await pb.collection("users").update(props.importedID, userData);
  const showAddCardDialog = (
    message: string,
    color = "primary",
    spinner = true
  ) => {
    addCardDialogMessage.value = {
      isVisible: true,
      message,
      color,
      spinner,
    };
  };

  isAddCardDialogVisible.value = true;
  showAddCardDialog("Adding card...");

  try {
    const updatedUser = await pb
      .collection("users")
      .update(`${props.importedID}`, {
        addCreditCard: true,
        addCardWebRedirect: "",
        addCardTransactionReff: "",
        webResponseLoc: window.location.origin,
      });

    if (updatedUser) {
      let elapsedTime = 0;
      const pollInterval = 1000;
      const maxTime = 5000;

      const poll = setInterval(async () => {
        elapsedTime += pollInterval;

        if (!isAddCardDialogVisible.value) {
          // Stop polling if the dialog is closed
          clearInterval(poll);

          return;
        }

        try {
          const userRecord = await pb
            .collection("users")
            .getOne(`${props.importedID}`);

          if (userRecord?.addCardWebRedirect) {
            window.location.href = userRecord.addCardWebRedirect;
            clearInterval(poll);
          } else if (elapsedTime >= maxTime) {
            showAddCardDialog(
              "Cannot add card, Server not responding.",
              "error",
              false
            );
          }
        } catch (pollError) {
          clearInterval(poll);
        }
      }, pollInterval);
    }
  } catch (error) {
    console.error("Error adding card: ", error);
    showAddCardDialog("Error adding card. Please try again.", "error", false);
  }
};

const closeAddCardDialog = async (): Promise<void> => {
  try {
    await pb.collection("users").update(`${props.importedID}`, {
      addCardWebRedirect: "",
      addCreditCard: false,
      addCardTransactionReff: "",
      webResponseLoc: "",
      status: "Active",
    });

    // Reset dialog visibility and message properties
    Object.assign(addCardDialogMessage.value, {
      isVisible: false,
      spinner: false,
      message: "",
      color: "",
    });
    isAddCardDialogVisible.value = false;
  } catch (error) {
    console.log("Error closing card add dialog,", error);
  }
};
</script>

<template>
  <div class="text-center pa-4">
    <VDialog :model-value="props.isDialogOpen" max-width="400" persistent>
      <VCard>
        <VCardTitle class="text-center">Welcome to our new Platform</VCardTitle>
        <VCardText class="py-2 text-center"
          >To continue, please verify your email address:<br />
          <strong>{{ props.email }}</strong
          >.
        </VCardText>
        <VForm ref="verifyStepVForm" class="px-4 pb-3">
          <VRow>
            <VCol cols="12" class="pb-0">
              <VTextField
                v-model="verificationCode"
                :rules="[requiredValidator]"
                :error-messages="errors.code"
                label="Enter the code"
              />
              &nbsp; &nbsp; Remember to check spam/junk folders
              <p class="mt-5 text-end" v-if="hasRequested">
                Did not receive a code?
                <span
                  class="text-primary cursor-pointer text-decoration-underline"
                  @click="handleCodeResend"
                  >Resend</span
                >
              </p>
            </VCol>
          </VRow>
        </VForm>
        <VDivider class="py-2" />
        <VCardActions>
          <VSpacer />
          <VBtn variant="flat" @click="handleValidation"> Verify </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>

  <VDialog v-model="verifyCC" max-width="400" persistent>
    <VCard>
      <VCardTitle class="text-center">Welcome to our new platform</VCardTitle>
      <VCardText class="py-2"
        >Credit card details could not be imported, please enter your card
        details.</VCardText
      >
      <VDivider class="py-2" />
      <VCardActions>
        <VSpacer />
        <VBtn variant="flat" @click="handleAddCard()"> Add Card </VBtn>
        <VBtn variant="flat" v-if="directDebitEnabled"> Add Direct Debit </VBtn>
        <span v-else style="width: 150px">&nbsp;</span>
        <span class="ps-7 pr-6"></span>
        <VBtn variant="flat" color="secondary" @click="closeVerifyCC()">
          Close
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>

  <!-- 👉 Add Card Dialog -->
  <VDialog
    :model-value="isAddCardDialogVisible"
    persistent
    max-width="600"
    max-height="800"
    no-click-animation
    @update:model-value="closeAddCardDialog"
  >
    <VCard>
      <div
        class="d-flex align-center justify-space-between bg-dialogHeaderColor"
      >
        <VCardTitle> Add Card </VCardTitle>

        <VBtn
          icon="mdi-close"
          size="large"
          variant="text"
          class="rounded-0"
          color=""
          @click="isAddCardDialogVisible = false"
        />
      </div>

      <VCardText class="text-center">
        <VProgressCircular
          v-if="addCardDialogMessage.spinner && addCardDialogMessage.isVisible"
          :color="addCardDialogMessage.color"
          indeterminate
          :size="50"
          variant="outlined"
        />
        <p
          class="text-h5 my-5"
          :class="{
            'text-error': addCardDialogMessage.color === 'error',
            'text-success': addCardDialogMessage.color === 'success',
          }"
        >
          {{ addCardDialogMessage.message }}
        </p>
      </VCardText>

      <VCardActions>
        <VSpacer />
        <VBtn color="error" variant="flat" @click="closeAddCardDialog">
          Close
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>
